import * as z from 'zod';

import { text } from '../envelope';
import { defineEvent, NO_CONTENT_IDS } from './define';

export type { EventDefinition, EventFamily } from './define';

/**
 * The reader events, schema version 1. Names are `object_action` and state
 * what the reader did or what happened, not which control was pressed. They
 * are English identifiers, not UI labels, and do not go through `@shared/i18n`.
 *
 * Only events the app emits are defined.
 */
export const catalog = {
	// App
	/** A route is shown. Carries the issue/article ids when the route shows one. */
	screen_view: defineEvent({
		family: 'app',
		params: z.strictObject({
			/** The route pattern, not the URL. */
			screen: text,
			path: text,
		}),
		requires: () => NO_CONTENT_IDS,
	}),
	app_install: defineEvent({
		family: 'app',
		params: z.strictObject({}),
		requires: () => NO_CONTENT_IDS,
	}),
	install_click: defineEvent({
		family: 'app',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	install_prompt_result: defineEvent({
		family: 'app',
		params: z.strictObject({ outcome: z.enum(['accepted', 'dismissed']) }),
		requires: () => NO_CONTENT_IDS,
	}),
	/**
	 * The reader changed one of their own settings. `setting` is the
	 * setting's id in the Settings dialog and `value` what it was set to.
	 * The app language has its own event, `language_change`, and the
	 * Analytics switch has none: switching sharing off is not shared.
	 */
	setting_change: defineEvent({
		family: 'app',
		params: z.strictObject({ setting: text, value: text }),
		requires: () => NO_CONTENT_IDS,
	}),

	// Account and access
	login_start: defineEvent({
		family: 'account',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	login: defineEvent({
		family: 'account',
		/** `method` is the tenant's auth type. */
		params: z.strictObject({ method: text }),
		requires: () => NO_CONTENT_IDS,
	}),

	/** A sign-in the reader submitted was refused. */
	login_fail: defineEvent({
		family: 'account',
		params: z.strictObject({ method: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	/** The reader signed out. */
	logout: defineEvent({
		family: 'account',
		params: z.strictObject({ method: text }),
		requires: () => NO_CONTENT_IDS,
	}),

	/**
	 * A gate stopped the reader. Recorded for every tenant, whether or not
	 * it has a paywall, so the funnel `paywall_hit` → `login_start` →
	 * `login` / `subscribe_click` can be read within a session.
	 */
	paywall_hit: defineEvent({
		family: 'account',
		params: z.strictObject({
			gate: z.enum(['route', 'issue', 'article']),
			reason: z.enum(['login_required', 'subscription_required', 'expired']),
		}),
		requires: ({ gate }) =>
			gate === 'article'
				? ['issue_id', 'article_id']
				: gate === 'issue'
					? ['issue_id']
					: NO_CONTENT_IDS,
	}),
	/** The reader followed the tenant's subscription link. */
	subscribe_click: defineEvent({
		family: 'account',
		params: z.strictObject({ location: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	/**
	 * The reader confirmed a plan on the subscriptions page and is being
	 * sent to the payment page. `price_id` is the plan's price id.
	 */
	checkout_start: defineEvent({
		family: 'account',
		params: z.strictObject({ price_id: text }),
		requires: () => NO_CONTENT_IDS,
	}),
	/**
	 * The reader came back from the payment page with the subscription
	 * confirmed. Once per return from checkout.
	 */
	subscribe: defineEvent({
		family: 'account',
		params: z.strictObject({}),
		requires: () => NO_CONTENT_IDS,
	}),

	// Reading
	/** An issue's reader opened. */
	issue_open: defineEvent({
		family: 'reading',
		params: z.strictObject({
			source: z.enum([
				'home',
				'preview',
				'section',
				'deeplink',
				'favorite',
				'downloads',
				'other',
			]),
		}),
		requires: () => ['issue_id'],
	}),
	/**
	 * An article became the active one (responsive layout). The article
	 * events carry `page_number`, the article's place in its issue, when it
	 * is read inside the issue; read on its own page (`/article/$id`) it has
	 * no place, so the field is absent (schema version 2).
	 */
	article_open: defineEvent({
		family: 'reading',
		params: z.strictObject({
			source: z.enum(['toc', 'swipe', 'deeplink', 'favorite', 'other']),
			/**
			 * How many articles the issue has, as this device saw it. With
			 * `page_number` it says how far through the issue the reader is.
			 */
			units_total: z.number().int().positive().optional(),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),
	/**
	 * The reader left an article: moved on, hid the tab or closed the page.
	 * It fires each time, so one reader can have several rows for one
	 * article; the reading side sums `dwell_ms` and takes the highest depth.
	 */
	article_read: defineEvent({
		family: 'reading',
		params: z.strictObject({
			/** Engaged time (`engaged-clock.ts`), whole milliseconds. */
			dwell_ms: z.number().int().nonnegative(),
			/** 0 to 1, two decimals. */
			max_scroll_depth: z.number().min(0).max(1),
			exit: z.enum(['next', 'back', 'hidden', 'closed']),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),
	/**
	 * The first time on this device that the article met the completion
	 * rule: read to at least 90% of its depth, with at least 10 seconds of
	 * engaged time, across however many visits that took (`thresholds.ts`).
	 */
	article_complete: defineEvent({
		family: 'reading',
		/** Engaged time on the article so far, whole milliseconds. */
		params: z.strictObject({ dwell_ms: z.number().int().nonnegative() }),
		requires: () => ['issue_id', 'article_id'],
	}),
	/**
	 * The first time on this device that the issue met the completion rule:
	 * 90% of its pages viewed (replica) or 80% of its articles opened
	 * (responsive). The units are pages or articles accordingly, and
	 * `units_total` is the issue's size as the device saw it.
	 */
	issue_complete: defineEvent({
		family: 'reading',
		params: z.strictObject({
			units_seen: z.number().int().nonnegative(),
			units_total: z.number().int().positive(),
		}),
		requires: () => ['issue_id'],
	}),
	/** A replica page was shown. One event per visible page. */
	issue_page_view: defineEvent({
		family: 'reading',
		params: z.strictObject({
			pages_visible: z.union([z.literal(1), z.literal(2)]),
			/** How many pages the issue has, as this device saw it. */
			units_total: z.number().int().positive().optional(),
		}),
		requires: () => ['issue_id', 'page_number'],
	}),
	/** The reader left a replica page. One event per visible page. */
	issue_page_leave: defineEvent({
		family: 'reading',
		params: z.strictObject({
			/** Engaged time the page was on screen, whole milliseconds. */
			dwell_ms: z.number().int().nonnegative(),
			pages_visible: z.union([z.literal(1), z.literal(2)]),
		}),
		requires: () => ['issue_id', 'page_number'],
	}),
	/** The reader opened the issue's table of contents. */
	toc_open: defineEvent({
		family: 'reading',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),
	/**
	 * The reader searched the issue's table of contents: once they stopped
	 * typing for a second (`SEARCH_SETTLE_MS`). `search_term` is what they
	 * typed, trimmed, lower-cased and cut to 100 characters;
	 * `results_count` how many articles matched.
	 */
	search: defineEvent({
		family: 'reading',
		params: z.strictObject({
			search_term: z.string().min(1).max(100),
			results_count: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id'],
	}),
	layout_change: defineEvent({
		family: 'reading',
		params: z.strictObject({ to: z.enum(['replica', 'responsive']) }),
		requires: () => ['issue_id'],
	}),
	language_change: defineEvent({
		family: 'reading',
		params: z.strictObject({ to: text, scope: z.enum(['app', 'issue']) }),
		requires: ({ scope }) => (scope === 'issue' ? ['issue_id'] : NO_CONTENT_IDS),
	}),

	// Engagement
	share: defineEvent({
		family: 'engagement',
		params: z.strictObject({
			method: z.enum(['clipboard', 'os']),
			shared_type: z.enum(['issue', 'article']),
		}),
		requires: ({ shared_type }) =>
			shared_type === 'article' ? ['issue_id', 'article_id'] : ['issue_id'],
	}),
	/** The reader asked for an issue to be kept for offline reading. */
	issue_download_start: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),
	/** An issue finished downloading for offline reading. */
	issue_download: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),
	/** A download that was started could not be finished. */
	issue_download_fail: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),
	/** The reader removed a downloaded issue. */
	issue_download_remove: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id'],
	}),
	/**
	 * The reader followed a link in an article's own content: a link in
	 * the text, a button, or a linked image. Adverts have `ad_click`.
	 * `link_host` is the host of a web address, never the full URL.
	 */
	link_click: defineEvent({
		family: 'engagement',
		params: z.strictObject({
			link_type: z.enum(['web', 'article', 'other']),
			component_type: z.enum(['text', 'button', 'image', 'other']),
			component_id: text.optional(),
			link_host: text.optional(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * The reader tapped a link area (a hotspot) on a replica page. The
	 * article is named when the page belongs to one. `link_host` is the
	 * host of the web address, never the full URL.
	 */
	hotspot_click: defineEvent({
		family: 'engagement',
		params: z.strictObject({ link_host: text.optional() }),
		requires: () => ['issue_id', 'page_number'],
	}),
	/**
	 * The reader moved a gallery to another slide. Once per slide per
	 * article view; the first slide is on screen without being moved to,
	 * so it has no row. `slide_number` starts at 1.
	 */
	gallery_slide: defineEvent({
		family: 'engagement',
		params: z.strictObject({
			gallery_id: text,
			slide_number: z.number().int().positive(),
			slides_total: z.number().int().positive(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * An embed from another site (a social post, a map, a chart, a video
	 * player) was at least half visible for a second. Once per article
	 * view. What the reader did inside it cannot be observed.
	 */
	embed_view: defineEvent({
		family: 'engagement',
		params: z.strictObject({ embed_id: text, embed_type: text }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),

	// Audio. The ids are those of the article being played, which is not
	// always the one on screen: playback continues while the reader moves on.
	/** Playback started or resumed. */
	audio_play: defineEvent({
		family: 'audio',
		params: z.strictObject({ position_ms: z.number().int().nonnegative() }),
		requires: () => ['issue_id', 'article_id'],
	}),
	/** Playback paused or stopped before the end. */
	audio_pause: defineEvent({
		family: 'audio',
		params: z.strictObject({
			/** Time actually played since the last `audio_play`, whole milliseconds. */
			listened_ms: z.number().int().nonnegative(),
			position_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),
	/** Playback reached the end. */
	audio_complete: defineEvent({
		family: 'audio',
		params: z.strictObject({
			listened_ms: z.number().int().nonnegative(),
			duration_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id'],
	}),

	// Video. Hosted videos only: a player embedded from another site is an
	// `embed_view`. A video that plays on its own, muted, is not recorded.
	/** Playback started or resumed. */
	video_play: defineEvent({
		family: 'video',
		params: z.strictObject({
			video_id: text,
			position_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/** Playback paused or stopped before the end. */
	video_pause: defineEvent({
		family: 'video',
		params: z.strictObject({
			video_id: text,
			/** Time actually played since the last `video_play`, whole milliseconds. */
			watched_ms: z.number().int().nonnegative(),
			position_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/** Playback reached the end. */
	video_complete: defineEvent({
		family: 'video',
		params: z.strictObject({
			video_id: text,
			watched_ms: z.number().int().nonnegative(),
			duration_ms: z.number().int().nonnegative(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),

	// Ads. `ad_id` is the advert component's id in the article; `ad_host` is
	// the host of where it links to, never the full URL.
	/** An image advert was at least half visible for a second. Once per article view. */
	ad_impression: defineEvent({
		family: 'ads',
		params: z.strictObject({ ad_id: text, ad_host: text.optional() }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/**
	 * The reader opened an image advert's link: a web address (`ad_host`
	 * says where) or another article of the app.
	 */
	ad_click: defineEvent({
		family: 'ads',
		params: z.strictObject({
			ad_id: text,
			link_type: z.enum(['web', 'article']),
			ad_host: text.optional(),
		}),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),
	/** A Google ad unit rendered. Not an impression: Google measures those. */
	ad_slot_render: defineEvent({
		family: 'ads',
		params: z.strictObject({ ad_id: text, slot_id: text }),
		requires: () => ['issue_id', 'article_id', 'page_number'],
	}),

	favorite_add: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id', 'article_id'],
	}),
	/** The reader removed a bookmark. */
	favorite_remove: defineEvent({
		family: 'engagement',
		params: z.strictObject({}),
		requires: () => ['issue_id', 'article_id'],
	}),
};

export type EventName = keyof typeof catalog;
export type EventParams<N extends EventName> = z.input<
	(typeof catalog)[N]['params']
>;

export const EVENT_NAMES = Object.keys(catalog) as Array<EventName>;
